import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Moon, Sun, Sunrise, Sunset, Search, Navigation, BookOpen, Heart, Loader, Play } from 'lucide-react';

const NamazTimes = ({ onSurahSelect }) => {
  const [locationName, setLocationName] = useState('Shirpur, Maharashtra');
  const [searchInput, setSearchInput] = useState('');
  
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const searchTimeoutRef = useRef(null);

  const [timings, setTimings] = useState(null);
  const [hijriDate, setHijriDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  const [nextPrayer, setNextPrayer] = useState({ name: '', time: '', diff: '' });
  const [activePrayerIndex, setActivePrayerIndex] = useState(-1);
  const [activePrayerName, setActivePrayerName] = useState('Isha');

  const postPrayerData = {
    'Fajr': {
      title: "فجر کے بعد کی فضیلت",
      recommended: "سورہ یٰسٓ اور فجر کے اذکار",
      duaAr: "اللَّهُمَّ أَجِرْنِي مِنَ النَّارِ",
      duaUr: "اے اللہ! مجھے جہنم کی آگ سے بچا۔ (۷ مرتبہ پڑھیں)",
      benefit: "حدیث: جو شخص فجر کی نماز کے بعد سورہ یٰسٓ پڑھے گا، اس کے دن بھر کے تمام کام آسان کر دیئے جائیں گے۔",
      actionParams: {
        title: "سورہ یٰسٓ اور فجر کے اذکار",
        isCombo: false, isRoutine: true,
        number: 36, englishName: 'Ya-Seen', englishNameTranslation: 'Ya Seen', name: 'يس', revelationType: 'Meccan', numberOfAyahs: 83,
        postAzkaar: { title: "فجر کے اذکار", arabic: "اللَّهُمَّ إِنِّي أَسْأَلُكَ عِلْمًا نَافِعًا، وَرِزْقًا طَيِّبًا، وَعَمَلًا مُتَقَبَّلًا", urdu: "اے اللہ! میں تجھ سے نفع بخش علم، پاکیزہ رزق اور قبول ہونے والے عمل کا سوال کرتا ہوں۔", benefit: "یہ دعا نبی کریم ﷺ فجر کی نماز کے بعد کثرت سے پڑھا کرتے تھے۔ اس سے دن بھر برکت رہتی ہے۔" }
      }
    },
    'Sunrise': {
      title: "اشراق کا وقت", recommended: "تسبیح اور ذکر", duaAr: "سُبْحَانَ اللَّهِ وَبِحَمْدِهِ", duaUr: "سبحان اللہ وبحمدہ (۱۰۰ مرتبہ پڑھیں)", benefit: "جو شخص فجر پڑھ کر ذکر کرتا رہے اور سورج نکلنے کے بعد دو رکعت اشراق پڑھے، اسے ایک مکمل حج اور عمرہ کا ثواب ملتا ہے۔", actionParams: null
    },
    'Dhuhr': {
      title: "ظہر کے بعد کی فضیلت", recommended: "آیۃ الکرسی اور تسبیح فاطمہ", duaAr: "اللَّهُمَّ أَنْتَ السَّلَامُ وَمِنْكَ السَّلَامُ، تَبَارَكْتَ يَا ذَا الْجَلَالِ وَالْإِكْرَامِ", duaUr: "۳۳ بار سبحان اللہ، ۳۳ بار الحمد للہ، ۳۴ بار اللہ اکبر", benefit: "حدیث: ہر فرض نماز کے بعد آیۃ الکرسی پڑھنے والے کو جنت میں جانے سے صرف موت روکے ہوئے ہے۔",
      actionParams: {
        title: "آیۃ الکرسی اور تسبیح", isCombo: true, isRoutine: true, comboTitle: "ظہر کے معمولات", englishName: "Dhuhr Actions", items: [{ number: 2, englishName: 'Al-Baqarah', name: 'البقرة', ayahRange: [255, 255], customTitle: 'آیۃ الکرسی' }],
        postAzkaar: { title: "تسبیح فاطمہ زہراء", arabic: "سُبْحَانَ اللَّهِ (33)، الْحَمْدُ لِلَّهِ (33)، اللَّهُ أَكْبَرُ (34)", urdu: "سبحان اللہ 33 بار، الحمدللہ 33 بار، اللہ اکبر 34 بار", benefit: "یہ دنیاوی تھکاوٹ دور کرتا ہے اور بے پناہ اجر کا باعث ہے۔" }
      }
    },
    'Asr': {
      title: "عصر کے بعد کی فضیلت", recommended: "سورہ النبأ اور استغفار", duaAr: "أَسْتَغْفِرُ اللَّهَ الْعَظِيمَ الَّذِي لَا إِلَهَ إِلَّا هُوَ الْحَيَّ الْقَيُّومَ وَأَتُوبُ إِلَيْهِ", duaUr: "میں اللہ سے معافی مانگتا ہوں جو زندہ اور قائم رہنے والا ہے۔", benefit: "عصر کے بعد کا وقت فرشتوں کی ڈیوٹی تبدیل ہونے کا ہوتا ہے، اس وقت ذکر کی بہت فضیلت ہے۔",
      actionParams: {
        title: "سورہ النبأ اور اذکار", isCombo: false, isRoutine: true, number: 78, englishName: 'An-Naba', name: 'النبأ', revelationType: 'Meccan', numberOfAyahs: 40,
        postAzkaar: { title: "شام کے اذکار", arabic: "أَمْسَيْنَا وَأَمْسَى الْمُلْكُ لِلَّهِ", urdu: "ہم نے اور تمام بادشاہی نے اللہ کے لیے شام کی۔", benefit: "سورہ النبأ کے بعد پڑھنے سے رات بھر حفاظت رہتی ہے۔" }
      }
    },
    'Maghrib': {
      title: "مغرب کے بعد کی فضیلت", recommended: "سورہ الواقعہ اور دعا", duaAr: "اللَّهُمَّ إِنِّي أَسْأَلُكَ الْجَنَّةَ وَأَعُوذُ بِكَ مِنَ النَّارِ", duaUr: "اے اللہ! میں تجھ سے جنت کا سوال کرتا ہوں اور آگ سے تیری پناہ مانگتا ہوں۔", benefit: "حدیث: جو شخص ہر رات سورہ واقعہ پڑھے گا، اسے کبھی فاقہ نہیں پہنچے گا۔",
      actionParams: {
        title: "سورہ الواقعہ اور دعا", isCombo: false, isRoutine: true, number: 56, englishName: "Al-Waqi'a", name: 'الواقعة', revelationType: 'Meccan', numberOfAyahs: 96,
        postAzkaar: { title: "مغرب کی دعا", arabic: "رَضِيتُ بِاللَّهِ رَبًّا، وَبِالْإِسْلَامِ دِينًا", urdu: "میں اللہ کے رب ہونے پر اور اسلام کے دین ہونے پر راضی ہو گیا۔", benefit: "قیامت کے دن اللہ اسے ضرور راضی کر دے گا۔" }
      }
    },
    'Isha': {
      title: "عشاء کے بعد کی فضیلت",
      recommended: "سورۃ الملک اور آمن الرسول",
      duaAr: "بِاسْمِكَ رَبِّ وَضَعْتُ جَنْبِي وَبِكَ أَرْفَعُهُ",
      duaUr: "اے میرے رب! تیرے ہی نام سے میں نے اپنا پہلو بچھایا اور تیرے ہی نام سے اٹھاؤں گا۔",
      benefit: "حدیث: سورۃ الملک قبر کے عذاب سے بچاتی ہے۔ اور جو شخص رات کو سورۃ البقرہ کی آخری ۲ آیات (آمن الرسول) پڑھ لے، تو وہ اسے ہر آفت سے بچانے کے لیے کافی ہو جاتی ہیں۔",
      actionParams: {
        title: "سورۃ الملک اور آمن الرسول",
        isCombo: true, 
        isRoutine: true, 
        comboTitle: "عشاء اور سونے کے معمولات", 
        englishName: "Isha Actions", 
        items: [
          { number: 67, englishName: 'Al-Mulk', name: 'الملك', customTitle: 'سورۃ الملک' }, 
          { number: 2, englishName: 'Al-Baqarah', name: 'البقرة', ayahRange: [285, 286], customTitle: 'آمن الرسول' }
        ],
        postAzkaar: { 
          title: "سونے کی دعا", 
          arabic: "اللَّهُمَّ بِاسْمِكَ أَمُوتُ وَأَحْيَا", 
          urdu: "اے اللہ! میں تیرے ہی نام کے ساتھ مرتا (سوتا) ہوں اور جیتا (جاگتا) ہوں۔", 
          benefit: "سنت: بستر پر دائیں کروٹ لیٹ کر یہ دعا پڑھیں، اللہ تعالیٰ رات بھر حفاظت فرمائے گا۔" 
        }
      }
    }
  };

  const urduPrayerNames = {
      'Fajr': 'فجر', 'Sunrise': 'اشراق', 'Dhuhr': 'ظہر', 
      'Asr': 'عصر', 'Maghrib': 'مغرب', 'Isha': 'عشاء'
  };

  const fetchPrayerTimesByCoords = async (lat, lon, displayName) => {
    setIsLoading(true); setShowDropdown(false);
    try {
      const response = await fetch(`https://api.aladhan.com/v1/timings?latitude=${lat}&longitude=${lon}&method=1&school=1`);
      const data = await response.json();
      if (data.code === 200) {
        setTimings(data.data.timings);
        setHijriDate(`${data.data.date.hijri.day} ${data.data.date.hijri.month.en}, ${data.data.date.hijri.year}`);
        setLocationName(displayName); setSearchInput('');
        localStorage.setItem('saved_namaz_location', JSON.stringify({ lat, lon, displayName }));
      }
    } catch (error) { alert("وقت لانے میں مسئلہ ہوا، اپنا انٹرنیٹ چیک کریں۔"); } finally { setIsLoading(false); }
  };

  const handleAutoDetect = () => {
    if (navigator.geolocation) {
      setIsLoading(true); setSearchInput('');
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=en`);
            const geoData = await res.json();
            const address = geoData.address || {};
            const placeName = address.city || address.town || address.county || address.state_district || "My Location";
            fetchPrayerTimesByCoords(latitude, longitude, `${placeName}, ${address.country || ''}`);
          } catch(e) { fetchPrayerTimesByCoords(latitude, longitude, "My Location"); }
        }, 
        (error) => { alert("لوکیشن کی اجازت نہیں ملی۔"); setIsLoading(false); }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 } 
      );
    } else { alert("آپ کا آلہ GPS سپورٹ نہیں کرتا۔"); }
  };

  const handleSearchInputChange = (e) => {
    const val = e.target.value; setSearchInput(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (val.trim().length > 2) {
      setIsSearching(true); setShowDropdown(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&addressdetails=1&limit=5&accept-language=en`);
          const data = await response.json(); setSuggestions(data);
        } catch (error) { console.error("Search error:", error); } finally { setIsSearching(false); }
      }, 600);
    } else { setSuggestions([]); setShowDropdown(false); setIsSearching(false); }
  };

  const handleSelectSuggestion = (place) => {
    const addr = place.address || {};
    const shortName = addr.city || addr.town || addr.county || place.display_name.split(',')[0];
    fetchPrayerTimesByCoords(place.lat, place.lon, `${shortName}, ${addr.country || ''}`);
  };

  useEffect(() => {
    const savedLocation = localStorage.getItem('saved_namaz_location');
    if (savedLocation) {
      try { const { lat, lon, displayName } = JSON.parse(savedLocation); fetchPrayerTimesByCoords(lat, lon, displayName); } catch (e) { fetchPrayerTimesByCoords(21.3463, 74.8780, 'Shirpur, India'); }
    } else { fetchPrayerTimesByCoords(21.3463, 74.8780, 'Shirpur, India'); }
  }, []);

  const format12Hour = (time24) => {
    if (!time24) return "";
    let [hours, minutes] = time24.split(':'); hours = parseInt(hours);
    const ampm = hours >= 12 ? 'PM' : 'AM'; hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  const getJamaatTime = (time24, offsetMinutes) => {
    if (!time24) return "";
    let [hours, minutes] = time24.split(':');
    let date = new Date(); date.setHours(parseInt(hours), parseInt(minutes) + offsetMinutes, 0);
    let jHours = date.getHours(); let jMins = date.getMinutes().toString().padStart(2, '0');
    const ampm = jHours >= 12 ? 'PM' : 'AM'; jHours = jHours % 12 || 12;
    return `${jHours}:${jMins} ${ampm}`;
  };

  useEffect(() => {
    if (!timings) return;
    const timer = setInterval(() => {
      const now = new Date(); const currentHours = now.getHours(); const currentMinutes = now.getMinutes();
      const currentTimeStr = `${currentHours.toString().padStart(2, '0')}:${currentMinutes.toString().padStart(2, '0')}`;
      const schedule = [ { id: 0, name: 'Fajr', time24: timings.Fajr }, { id: 1, name: 'Sunrise', time24: timings.Sunrise }, { id: 2, name: 'Dhuhr', time24: timings.Dhuhr }, { id: 3, name: 'Asr', time24: timings.Asr }, { id: 4, name: 'Maghrib', time24: timings.Maghrib }, { id: 5, name: 'Isha', time24: timings.Isha } ];
      let nextP = schedule[0]; let activeIdx = -1; let activeName = 'Isha'; 
      for (let i = 0; i < schedule.length; i++) {
        if (currentTimeStr < schedule[i].time24) { nextP = schedule[i]; activeIdx = i > 0 ? i - 1 : 5; activeName = schedule[activeIdx].name; break; }
      }
      if (currentTimeStr >= schedule[5].time24) { activeIdx = 5; activeName = 'Isha'; nextP = schedule[0]; }
      setActivePrayerIndex(activeIdx); setActivePrayerName(activeName);

      let [nextH, nextM] = nextP.time24.split(':').map(Number);
      let diffH = nextH - currentHours; let diffM = nextM - currentMinutes; let diffS = 60 - now.getSeconds();
      if (diffS > 0) diffM -= 1; if (diffM < 0) { diffM += 60; diffH -= 1; } if (diffH < 0) diffH += 24; 
      const pad = (num) => num.toString().padStart(2, '0');
      setNextPrayer({ name: nextP.name, diff: `${pad(diffH)}:${pad(diffM)}:${pad(diffS)}` });
    }, 1000);
    return () => clearInterval(timer);
  }, [timings]);

  const prayerList = timings ? [
    { name: 'Fajr', time: timings.Fajr, icon: <Moon size={14} />, jamaatOffset: 30 },
    { name: 'Sunrise', time: timings.Sunrise, icon: <Sunrise size={14} />, jamaatOffset: null },
    { name: 'Dhuhr', time: timings.Dhuhr, icon: <Sun size={14} />, jamaatOffset: 15 },
    { name: 'Asr', time: timings.Asr, icon: <Sun size={14} />, jamaatOffset: 15 },
    { name: 'Maghrib', time: timings.Maghrib, icon: <Sunset size={14} />, jamaatOffset: 5 },
    { name: 'Isha', time: timings.Isha, icon: <Moon size={14} />, jamaatOffset: 15 },
  ] : [];

  const currentDua = postPrayerData[activePrayerName] || postPrayerData['Isha'];
  const currentPrayerDetails = prayerList.find(p => p.name === activePrayerName) || prayerList[5];

  return (
    <div className="namaz-container animate-fade-in" style={{direction: 'rtl', padding: '10px 10px 80px 10px'}}>
      
      <style>{`
        .premium-location-strip { margin-bottom: 10px; width: 100%; position: relative; z-index: 1000; direction: rtl; }
        .location-input-glass { display: flex; align-items: center; background: var(--glass-bg, #ffffff); border: 1px solid rgba(212, 175, 55, 0.4); border-radius: 20px; padding: 2px 6px 2px 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.05); }
        .icon-search-gold { color: var(--gold-primary); margin-right: 6px; margin-left: 4px; }
        .urdu-location-input { flex: 1; background: transparent; border: none; color: var(--text-primary); font-family: 'Noto Nastaliq Urdu', serif; font-size: 0.75rem; outline: none; padding: 2px 0; }
        .urdu-location-input::placeholder { color: var(--text-muted); }
        .vert-divider { width: 1px; height: 14px; background: rgba(212, 175, 55, 0.3); margin: 0 6px; }
        .gps-auto-btn { display: flex; align-items: center; gap: 4px; background: linear-gradient(135deg, #D4AF37, #C59B27); border: none; border-radius: 15px; padding: 4px 8px; color: #ffffff; cursor: pointer; box-shadow: 0 2px 5px rgba(212, 175, 55, 0.3); }
        .urdu-suggestions-dropdown { position: absolute; top: calc(100% + 5px); left: 0; right: 0; background: var(--glass-bg); border: 1px solid rgba(212, 175, 55, 0.3); border-radius: 10px; overflow: hidden; box-shadow: 0 5px 15px rgba(0,0,0,0.1); z-index: 1000; }
        .sugg-item-urdu { display: flex; align-items: center; gap: 8px; padding: 6px 10px; cursor: pointer; border-bottom: 1px solid rgba(0,0,0,0.05); }
        .sugg-main-title { font-family: 'Noto Nastaliq Urdu', serif; color: var(--gold-primary); font-size: 0.8rem; }
        .sugg-sub-title { font-family: 'Poppins', sans-serif; color: var(--text-muted); font-size: 0.6rem; direction: ltr; text-align: right; }
        
        .exact-fazeelat-card .dua-card-header h3 {
            font-size: 0.9rem !important; 
        }
        .exact-fazeelat-card .dua-arabic {
            font-size: 1.6rem !important; 
        }
        
        .matching-urdu-size {
            font-family: 'Noto Nastaliq Urdu', serif !important;
            font-size: 0.75rem !important;
            line-height: 1.8;
        }
      `}</style>
      
      <div className="premium-location-strip">
        <div className="location-input-glass">
            <Search size={12} className="icon-search-gold" />
            <input type="text" placeholder="شہر تلاش کریں..." value={searchInput} onChange={handleSearchInputChange} onFocus={() => { if(suggestions.length > 0) setShowDropdown(true); }} className="urdu-location-input" />
            <div className="vert-divider"></div>
            <button type="button" onClick={handleAutoDetect} className="gps-auto-btn" title="My Location"><Navigation size={12} /></button>
        </div>
        
        {showDropdown && (
          <div className="urdu-suggestions-dropdown animate-fade-in">
             {isSearching ? (
               <div style={{padding: '8px', textAlign: 'center', fontSize: '0.65rem', color: 'var(--text-muted)'}}><Loader className="spinner-icon" size={12} style={{verticalAlign: 'middle', marginLeft: '4px'}}/> تلاش جاری...</div>
             ) : suggestions.length > 0 ? (
               suggestions.map((place, idx) => {
                 const addr = place.address || {};
                 const shortName = addr.city || addr.town || addr.county || place.display_name.split(',')[0];
                 return (
                   <div key={idx} className="sugg-item-urdu" onClick={() => handleSelectSuggestion(place)}>
                     <MapPin size={12} style={{color:'var(--gold-primary)'}} />
                     <div style={{display: 'flex', flexDirection: 'column', width: '100%', textAlign: 'right'}}>
                        <span className="sugg-main-title">{shortName}</span>
                        <span className="sugg-sub-title">{shortName}, {addr.country || ''}</span>
                     </div>
                   </div>
                 );
               })
             ) : searchInput.length > 2 ? (
               <div style={{padding: '8px', textAlign: 'center', fontSize: '0.65rem', color: 'var(--text-muted)'}}>کوئی مقام نہیں ملا۔</div>
             ) : null}
          </div>
        )}
      </div>

      {isLoading ? (
         <div className="loading-screen" style={{height: '40vh'}}><div className="premium-loader"></div></div>
      ) : (
        <>
          <div className="next-prayer-card" onClick={() => setShowDropdown(false)} style={{padding: '12px', borderRadius: '12px'}}>
            <div className="prayer-info-top" style={{flexDirection: 'row-reverse', marginBottom: '8px'}}>
              <span className="location-badge" style={{direction: 'ltr', fontSize: '0.6rem', padding: '2px 6px'}}>
                <MapPin size={10} style={{marginRight: '2px'}} /> {locationName.split(',')[0]}
              </span>
              <span className="hijri-date" style={{fontFamily: "'Poppins', sans-serif", fontSize: '0.65rem'}}>{hijriDate}</span>
            </div>
            
            <div className="main-time-display" style={{padding: '10px 0'}}>
              <h2 className="next-prayer-name" style={{fontFamily: "'Noto Nastaliq Urdu', serif", fontSize: '0.8rem', color: 'var(--gold-primary)', margin: '0 0 2px 0'}}>
                  {urduPrayerNames[nextPrayer.name]} شروع ہونے میں:
              </h2>
              <h1 className="countdown-timer" style={{direction: 'ltr', fontSize: '1.8rem', margin: 0}}>{nextPrayer.diff || "00:00:00"}</h1>
            </div>

            {timings && nextPrayer.name && (
                <div className="next-prayer-detailed-info animate-fade-in" style={{marginTop: '8px', textAlign: 'center', zIndex: 5, position: 'relative'}}>
                    <div style={{display: 'flex', justifyContent: 'center', gap: '8px'}}>
                        <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(212, 175, 55, 0.2)', borderRadius: '6px', padding: '2px 8px', minWidth: '50px' }}>
                            <span style={{color: 'var(--gold-primary)', fontFamily: "'Noto Nastaliq Urdu', serif", fontSize: '0.8rem', display: 'block'}}>آذان</span>
                            <span style={{color: '#fff', fontWeight: 'bold', fontSize: '0.9rem'}}>{format12Hour(timings[nextPrayer.name])}</span>
                        </div>
                        {(() => {
                           const pDetails = prayerList.find(p => p.name === nextPrayer.name);
                           if (pDetails && pDetails.jamaatOffset !== null) {
                               return (
                                   <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(212, 175, 55, 0.2)', borderRadius: '6px', padding: '2px 8px', minWidth: '50px' }}>
                                        <span style={{color: 'var(--gold-primary)', fontFamily: "'Noto Nastaliq Urdu', serif", fontSize: '0.8rem', display: 'block'}}>جماعت</span>
                                        <span style={{color: '#fff', fontWeight: 'bold', fontSize: '0.9rem'}}>{getJamaatTime(timings[nextPrayer.name], pDetails.jamaatOffset)}</span>
                                   </div>
                               );
                           }
                           return null;
                        })()}
                    </div>
                </div>
            )}
            
            {/* ✅ YAHAN NOTE ADD KIYA GAYA HAI */}
            <div style={{marginTop: '12px', zIndex: 5, position: 'relative', background: 'rgba(128,128,128,0.05)', padding: '6px', borderRadius: '8px', border: '1px dashed var(--glass-border)'}}>
               <p style={{
                   margin: 0, 
                   fontFamily: "'Noto Nastaliq Urdu', serif", 
                   fontSize: '0.65rem', 
                   color: 'var(--text-secondary)', /* CSS Variable Light/Dark mode dono ko khud sambhale ga */
                   lineHeight: '1.6',
                   textAlign: 'center'
               }}>
                   نوٹ: نماز اور جماعت کے وقت میں کچھ منٹ آگے یا پیچھے ہو سکتے ہیں، اس لیے اپنی قریب کی مسجد سے ایک بار دیکھ لیجیے گا۔
               </p>
            </div>

            <div className="pattern-overlay"></div>
          </div>

          <div className="dynamic-dua-card exact-fazeelat-card" onClick={() => setShowDropdown(false)} style={{marginBottom: '10px', borderRadius: '10px'}}>
            <div className="attached-prayer-time" style={{padding: '6px 10px'}}>
               <div className="attached-pt-right">
                  <div className="icon-circle-small" style={{width: '20px', height: '20px'}}>{currentPrayerDetails.icon}</div>
                  <span className="attached-pt-name" style={{fontSize: '0.75rem', paddingTop: '2px'}}>{urduPrayerNames[currentPrayerDetails.name]}</span>
               </div>
               <div className="attached-pt-left" style={{gap: '10px'}}>
                  {currentPrayerDetails.jamaatOffset !== null && (
                    <div className="apt-box jamaat-box-attached" style={{paddingLeft: '6px'}}>
                       <small style={{fontSize: '0.75rem', fontFamily: "'Noto Nastaliq Urdu', serif", color: 'var(--text-muted)'}}>جماعت</small>
                       <strong style={{fontSize: '0.8rem'}}>{getJamaatTime(currentPrayerDetails.time, currentPrayerDetails.jamaatOffset)}</strong>
                    </div>
                  )}
                  <div className="apt-box">
                     <small style={{fontSize: '0.75rem', fontFamily: "'Noto Nastaliq Urdu', serif", color: 'var(--text-muted)'}}>آذان</small>
                     <strong style={{fontSize: '0.8rem'}}>{format12Hour(currentPrayerDetails.time)}</strong>
                  </div>
               </div>
            </div>

            <div className="dua-card-header" style={{padding: '6px 10px 4px 10px'}}>
               <BookOpen size={12} className="gold-icon" />
               <h3>{currentDua.title}</h3>
            </div>
            <div className="dua-card-body" style={{padding: '6px 10px 12px 10px'}}>
               <div className="surah-badge matching-urdu-size" style={{padding: '2px 6px', marginBottom: '8px'}}>تجویز کردہ: <strong style={{marginRight: '4px'}}>{currentDua.recommended}</strong></div>
               
               <p className="dua-arabic" style={{margin: '0 0 6px 0', lineHeight: '1.5'}}>{currentDua.duaAr}</p>
               <p className="dua-urdu" style={{fontSize: '0.65rem', margin: '0 0 8px 0', lineHeight: '1.6'}}>{currentDua.duaUr}</p>
               
               {currentDua.benefit && (
                   <p className="matching-urdu-size" style={{color: 'var(--gold-shiny)', margin: '0 0 8px 0', fontWeight: 'bold', textShadow: '0 0 5px rgba(212, 175, 55, 0.3)'}}>
                       {currentDua.benefit}
                   </p>
               )}
               
               {currentDua.actionParams && (
                  <button className="unified-premium-btn animate-fade-in" onClick={() => onSurahSelect(currentDua.actionParams)} style={{padding: '6px 12px', marginTop: '6px', borderRadius: '15px'}}>
                     <Play size={12} fill="currentColor" className="btn-action-icon" /> 
                     <span className="unified-btn-text" style={{fontSize: '0.7rem'}}>{currentDua.actionParams.title}</span>
                  </button>
               )}
            </div>
          </div>
          
          <div className="prayer-timeline" onClick={() => setShowDropdown(false)} style={{direction: 'ltr', gap: '6px'}}>
            {prayerList.map((prayer, index) => {
              const isActive = index === activePrayerIndex;
              const isPassed = index < activePrayerIndex;
              return (
                <div key={index} className={`prayer-row ${isActive ? 'active-prayer' : ''} ${isPassed ? 'passed-prayer' : ''}`} style={{padding: '6px 8px', borderRadius: '8px'}}>
                  <div className="prayer-icon-col">
                    <div className="icon-circle" style={{width: '22px', height: '22px'}}>{prayer.icon}</div>
                  </div>
                  <div className="prayer-details-col" style={{gap: '6px'}}>
                    <span className="prayer-name-text" style={{fontSize: '0.75rem'}}>{urduPrayerNames[prayer.name]}</span>
                    
                    <div className="dual-times-box" style={{gap: '8px'}}>
                       <div className="time-item">
                           <small style={{fontSize: '0.75rem', fontFamily: "'Noto Nastaliq Urdu', serif", color: 'var(--text-muted)'}}>آذان</small>
                           <strong style={{fontSize: '0.85rem'}}>{format12Hour(prayer.time)}</strong>
                       </div>
                       
                       {prayer.jamaatOffset !== null && (
                         <div className="time-item jamaat-item" style={{paddingRight: '8px'}}>
                             <small style={{fontSize: '0.75rem', fontFamily: "'Noto Nastaliq Urdu', serif", color: 'var(--text-muted)'}}>جماعت</small>
                             <strong style={{fontSize: '0.85rem'}}>{getJamaatTime(prayer.time, prayer.jamaatOffset)}</strong>
                         </div>
                       )}
                       
                       {prayer.name === 'Sunrise' && (
                         <div className="time-item jamaat-item" style={{opacity: 0.5, paddingRight: '8px'}}>
                             <small style={{fontSize: '0.75rem', fontFamily: "'Noto Nastaliq Urdu', serif", color: 'var(--text-muted)'}}>اشراق</small>
                             <strong style={{fontSize: '0.85rem'}}>{getJamaatTime(prayer.time, 15)}</strong>
                         </div>
                       )}
                    </div>

                  </div>
                  {isActive && <div className="active-indicator" style={{fontSize: '0.55rem', padding: '2px 6px', borderRadius: '8px'}}><span>جاری ہے</span></div>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default NamazTimes;